<template>
  <div class="gestion-equipo-container">
    <!-- Header -->
    <div class="equipo-header mb-4">
      <div class="flex items-center gap-3">
        <div class="icon-header">
          <i class="feather icon-users"></i>
        </div>
        <div>
          <h4 class="font-bold text-gray-800 m-0">Mi Equipo — {{ departamento ? departamento.tdp_nombre : 'Área' }}</h4>
          <p class="text-sm text-gray-500 m-0">Gestiona quién pertenece a tu departamento</p>
        </div>
      </div>
    </div>

    <div class="vx-row">
      <!-- Columna izquierda: Sin departamento -->
      <div class="vx-col w-full md:w-1/2 mb-4">
        <vx-card>
          <div class="card-section-title flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <vs-chip color="warning">{{ empleadosSinDepto.length }}</vs-chip>
              <span class="font-semibold text-gray-700">Sin departamento asignado</span>
            </div>
            <vx-tooltip text="Empleados de nómina que aún no tienen área">
              <i class="feather icon-info text-gray-400"></i>
            </vx-tooltip>
          </div>

          <!-- Buscador izquierda -->
          <vs-input
            v-model="busquedaSin"
            placeholder="Buscar empleado..."
            icon="search"
            class="w-full mb-3"
          />

          <!-- Loading -->
          <div v-if="cargandoSin" class="flex justify-center py-8 text-gray-400">
            <i class="feather icon-loader animate-spin mr-2"></i>
            <span class="text-sm">Cargando...</span>
          </div>

          <div v-else-if="!empleadosSinDeptoFiltrados.length" class="text-center py-8 text-gray-400 text-sm">
            <i class="feather icon-check-circle text-green-400 text-3xl block mb-2"></i>
            Todos los colaboradores tienen área asignada
          </div>

          <div v-else class="employee-list">
            <div
              v-for="emp in empleadosSinDeptoFiltrados"
              :key="emp.idusuario"
              class="employee-card employee-card--unassigned"
            >
              <div class="employee-info">
                <div class="employee-avatar">{{ iniciales(emp.nombre_completo) }}</div>
                <div class="employee-details">
                  <span class="employee-name">{{ emp.nombre_completo }}</span>
                  <span class="employee-meta">{{ emp.cedula || emp.email }}</span>
                </div>
              </div>
              <vx-tooltip :text="`Agregar a ${departamento ? departamento.tdp_nombre : 'mi área'}`">
                <vs-button
                  size="small"
                  color="success"
                  type="filled"
                  :disabled="asignando === emp.idusuario"
                  @click="asignarAMiArea(emp)"
                >
                  <i v-if="asignando === emp.idusuario" class="feather icon-loader animate-spin"></i>
                  <i v-else class="feather icon-user-plus"></i>
                </vs-button>
              </vx-tooltip>
            </div>
          </div>
        </vx-card>
      </div>

      <!-- Columna derecha: Mi departamento -->
      <div class="vx-col w-full md:w-1/2 mb-4">
        <vx-card>
          <div class="card-section-title flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <vs-chip color="primary">{{ empleadosDepto.length }}</vs-chip>
              <span class="font-semibold text-gray-700">En mi departamento</span>
            </div>
            <vx-tooltip text="Colaboradores actualmente en tu área">
              <i class="feather icon-info text-gray-400"></i>
            </vx-tooltip>
          </div>

          <!-- Buscador derecha -->
          <vs-input
            v-model="busquedaDepto"
            placeholder="Buscar empleado..."
            icon="search"
            class="w-full mb-3"
          />

          <!-- Loading -->
          <div v-if="cargandoDepto" class="flex justify-center py-8 text-gray-400">
            <i class="feather icon-loader animate-spin mr-2"></i>
            <span class="text-sm">Cargando...</span>
          </div>

          <div v-else-if="!empleadosDeptoFiltrados.length" class="text-center py-8 text-gray-400 text-sm">
            <i class="feather icon-users text-2xl block mb-2"></i>
            No hay colaboradores en tu área aún
          </div>

          <div v-else class="employee-list">
            <div
              v-for="emp in empleadosDeptoFiltrados"
              :key="emp.idusuario"
              class="employee-card employee-card--assigned"
            >
              <div class="employee-info">
                <div class="employee-avatar employee-avatar--primary">{{ iniciales(emp.nombre_completo) }}</div>
                <div class="employee-details">
                  <span class="employee-name">{{ emp.nombre_completo }}</span>
                  <span class="employee-meta">{{ emp.cedula || emp.email }}</span>
                </div>
              </div>
              <vx-tooltip text="Quitar de mi área (pasará a sin departamento)">
                <vs-button
                  size="small"
                  color="danger"
                  type="flat"
                  :disabled="asignando === emp.idusuario"
                  @click="quitarDeArea(emp)"
                >
                  <i v-if="asignando === emp.idusuario" class="feather icon-loader animate-spin"></i>
                  <i v-else class="feather icon-user-minus"></i>
                </vs-button>
              </vx-tooltip>
            </div>
          </div>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GestionEquipoDepartamento',
  props: {
    departamento: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      empleadosSinDepto: [],
      empleadosDepto: [],
      cargandoSin: false,
      cargandoDepto: false,
      busquedaSin: '',
      busquedaDepto: '',
      asignando: null
    }
  },
  computed: {
    empleadosSinDeptoFiltrados() {
      const q = this.busquedaSin.toLowerCase()
      if (!q) return this.empleadosSinDepto
      return this.empleadosSinDepto.filter(e =>
        (e.nombre_completo || '').toLowerCase().includes(q) ||
        (e.cedula || '').includes(q) ||
        (e.email || '').toLowerCase().includes(q)
      )
    },
    empleadosDeptoFiltrados() {
      const q = this.busquedaDepto.toLowerCase()
      if (!q) return this.empleadosDepto
      return this.empleadosDepto.filter(e =>
        (e.nombre_completo || '').toLowerCase().includes(q) ||
        (e.cedula || '').includes(q) ||
        (e.email || '').toLowerCase().includes(q)
      )
    }
  },
  watch: {
    departamento: {
      immediate: true,
      handler(val) {
        if (val && val.tdp_id) {
          this.cargarDatos()
        }
      }
    }
  },
  methods: {
    async cargarDatos() {
      await Promise.all([
        this.cargarSinDepartamento(),
        this.cargarMiEquipo()
      ])
    },
    async cargarSinDepartamento() {
      this.cargandoSin = true
      try {
        const res = await this.$http.get(this.$server_url + 'actividades-control/empleados/sin-departamento')
        this.empleadosSinDepto = res.data || []
      } catch (e) {
        console.error(e)
        this.$vs.notify({ title: 'Error', text: 'No se pudo cargar la lista de colaboradores sin área', color: 'danger' })
      } finally {
        this.cargandoSin = false
      }
    },
    async cargarMiEquipo() {
      if (!this.departamento) return
      this.cargandoDepto = true
      try {
        const res = await this.$http.get(this.$server_url + `actividades-control/empleados/departamento/${this.departamento.tdp_id}`)
        this.empleadosDepto = res.data || []
      } catch (e) {
        console.error(e)
        this.$vs.notify({ title: 'Error', text: 'No se pudo cargar el equipo del área', color: 'danger' })
      } finally {
        this.cargandoDepto = false
      }
    },
    async asignarAMiArea(emp) {
      if (!this.departamento) return
      this.asignando = emp.idusuario
      try {
        await this.$http.put(
          this.$server_url + `actividades-control/empleados/${emp.idusuario}/departamento`,
          { tdp_id: this.departamento.tdp_id }
        )
        // Mover de la lista sin-depto a mi equipo
        this.empleadosSinDepto = this.empleadosSinDepto.filter(e => e.idusuario !== emp.idusuario)
        this.empleadosDepto.push({ ...emp, tdp_id: this.departamento.tdp_id })
        this.$vs.notify({
          title: '¡Listo!',
          text: `${emp.nombre_completo} fue agregado a ${this.departamento.tdp_nombre}`,
          color: 'success',
          iconPack: 'feather',
          icon: 'icon-user-check'
        })
      } catch (e) {
        console.error(e)
        this.$vs.notify({ title: 'Error', text: 'No se pudo asignar el colaborador', color: 'danger' })
      } finally {
        this.asignando = null
      }
    },
    async quitarDeArea(emp) {
      this.asignando = emp.idusuario
      try {
        await this.$http.put(
          this.$server_url + `actividades-control/empleados/${emp.idusuario}/departamento`,
          { tdp_id: null }
        )
        // Mover de mi equipo a sin-depto
        this.empleadosDepto = this.empleadosDepto.filter(e => e.idusuario !== emp.idusuario)
        this.empleadosSinDepto.push({ ...emp, tdp_id: null, departamento_nombre: 'Sin departamento' })
        this.$vs.notify({
          title: 'Removido',
          text: `${emp.nombre_completo} fue removido del área`,
          color: 'warning',
          iconPack: 'feather',
          icon: 'icon-user-minus'
        })
      } catch (e) {
        console.error(e)
        this.$vs.notify({ title: 'Error', text: 'No se pudo remover el colaborador', color: 'danger' })
      } finally {
        this.asignando = null
      }
    },
    iniciales(nombre) {
      if (!nombre) return '?'
      const parts = nombre.trim().split(' ')
      return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase()
    }
  }
}
</script>

<style scoped>
.equipo-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 16px 20px;
  color: white;
}
.equipo-header h4 { color: white; }
.equipo-header p { color: rgba(255,255,255,0.8); }
.icon-header {
  width: 44px; height: 44px;
  background: rgba(255,255,255,0.2);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
}

.employee-list {
  max-height: 420px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.employee-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 8px;
  transition: background 0.15s;
}
.employee-card--unassigned {
  background: #fff8f0;
  border: 1px solid #fed7aa;
}
.employee-card--unassigned:hover { background: #fff1e0; }
.employee-card--assigned {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}
.employee-card--assigned:hover { background: #dcfce7; }

.employee-info { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; }
.employee-avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700;
  flex-shrink: 0;
}
.employee-avatar--primary {
  background: #dbeafe;
  color: #1d4ed8;
}
.employee-details { display: flex; flex-direction: column; min-width: 0; }
.employee-name {
  font-size: 13px; font-weight: 600;
  color: #1e293b;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.employee-meta {
  font-size: 11px; color: #94a3b8;
}
.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
